<template>
  <div class="image-upload-container">
    <div class="image-preview-list">
      <div v-for="(preview, index) in previews" :key="index" class="image-preview-item">
        <img :src="preview.url" alt="预览" class="preview-img">
        <div class="preview-remove" @click="removePreview(index)">
          <i class="el-icon-close"></i>
        </div>
        <div class="item-progress" v-if="preview.uploading">
          <el-progress :percentage="preview.progress" :stroke-width="2"></el-progress>
        </div>
      </div>
      <div class="image-preview-item add-preview" @click="triggerFileInput" v-if="previews.length < maxFiles">
        <div class="add-icon">
          <i class="el-icon-plus"></i>
        </div>
        <span class="add-text">{{ uploadText }}</span>
      </div>
    </div>
    <input ref="fileInput" type="file" accept="image/*" @change="handleFileChange" class="hidden-input" multiple>
  </div>
</template>
<script>
import { decrypt } from '@/utils/crypto.js';
export default {
  name: 'ImageUpload',
  props: {
    imageUrl: {
      type: [String, Array],
      default: ''
    },
    defaultImage: {
      type: String,
      default: ''
    },
    uploadText: {
      type: String,
      default: '上传图片'
    },
    maxFiles: {
      type: Number,
      default: 6
    },
    memberId: {
      type: [String, Number],
      default: '13759'
    }
  },
  data() {
    return {
      previews: [],
      isEncrypt: 1,
      uploadedUrls: []
    };
  },
  mounted() {
    this.handleImageUrl(this.imageUrl);
  },
  // 添加 watch 监听 imageUrl 的变化
  watch: {
    imageUrl(newVal) {
      // 清空现有预览
      this.previews = [];
      this.uploadedUrls = [];
      // 处理新的 imageUrl
      this.handleImageUrl(newVal);
    }
  },
  methods: {
    // 处理 imageUrl，支持字符串和数组
    handleImageUrl(url) {
      if (!url) return;

      if (Array.isArray(url)) {
        // 处理数组，每个元素都添加预览
        url.forEach(item => this.previews.push({
          url: item,
          uploading: false,
          progress: 0
        }));
        this.uploadedUrls = url;
      } else {
        // 处理单个字符串，添加预览
        this.previews.push({
          url: url,
          uploading: false,
          progress: 0
        });
        this.uploadedUrls.push(url);
      }
      this.$emit('upload-success', this.uploadedUrls);
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleFileChange(e) {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;

      // 检查总文件数是否超过限制
      if (this.previews.length + files.length > this.maxFiles) {
        this.$message.error(`最多只能上传${this.maxFiles}张图片`);
        this.resetFileInput();
        return;
      }

      // 验证并准备上传每个文件
      const validFiles = [];
      for (const file of files) {
        // 检查文件大小（限制5MB）
        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
          this.$message.error(`${file.name} 图片大小不能超过5MB`);
          continue;
        }

        // 检查文件类型
        const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
        if (!validTypes.includes(file.type)) {
          this.$message.error(`${file.name} 只支持JPG、PNG、GIF格式的图片`);
          continue;
        }

        validFiles.push(file);
      }

      if (validFiles.length === 0) {
        this.resetFileInput();
        return;
      }

      // 为每个有效文件创建预览并开始上传
      validFiles.forEach(file => {
        this.createPreview(file);
      });

      this.resetFileInput();
    },
    createPreview(file) {
      // 创建文件预览URL
      const reader = new FileReader();
      reader.onload = (e) => {
        const previewUrl = e.target.result;
        const previewIndex = this.previews.length;

        // 添加预览到列表
        this.previews.push({
          url: previewUrl,
          uploading: true,
          progress: 10,
          file: file
        });

        // 开始上传
        this.startUpload(file, previewIndex);
      };
      reader.readAsDataURL(file);
    },
    async startUpload(file, previewIndex) {
      try {
        // 更新预览进度
        this.updatePreviewProgress(previewIndex, 10);

        // 生成符合示例格式的文件名：member_id/日期/文件名
        const date = new Date();
        const dateStr = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
        const filename = `m${this.memberId}/${dateStr}/${file.name}`;
        // 第一步：获取上传参数（先请求6454d9663e508接口）
        const paramsRes = await this.$api.getUploadParams({
          filename: filename,
          is_encrypt: this.isEncrypt,
          member_id: this.memberId
        });
        this.updatePreviewProgress(previewIndex, 30);
        if (!paramsRes) {
          throw new Error(paramsRes.msg || '获取上传参数失败');
        }
        // 解密返回的data值
        let decryptedData;
        try {
          decryptedData = decrypt(paramsRes);
          decryptedData = JSON.parse(decryptedData);
        } catch (error) {
          console.error('数据解密失败:', error);
          throw new Error('数据解密失败');
        }
        // 第二步：构造FormData上传图片
        const formData = new FormData();
        formData.append('module', ''); // 固定参数
        formData.append('dir', decryptedData.dir || ''); // 固定参数
        formData.append('file', file)

        // 模拟进度更新
        this.updatePreviewProgress(previewIndex, 50);

        // 第三步：上传图片（再请求5d5fa8984f0c2接口）
        const uploadRes = await this.$api.uploadImage(formData);

        this.updatePreviewProgress(previewIndex, 80);

        // if (uploadRes.code != 1) {
        //   throw new Error(uploadRes.msg || '图片上传失败');
        // }

        this.updatePreviewProgress(previewIndex, 100);

        // 上传成功，返回图片路径
        const uploadedImageUrl = uploadRes[0] && uploadRes[0].path;
        if (uploadedImageUrl) {
          // 更新预览为上传后的图片URL
          this.previews[previewIndex].url = uploadedImageUrl;
          this.previews[previewIndex].uploading = false;

          // 保存上传成功的URL
          this.uploadedUrls.push(uploadedImageUrl);

          // 检查是否所有图片都上传完成
          const allUploaded = this.previews.every(preview => !preview.uploading);
          if (allUploaded) {
            this.$emit('upload-success', this.uploadedUrls);
            this.$message.success('所有图片上传成功');
          }
        } else {
          throw new Error('上传成功但未获取到图片路径');
        }
      } catch (error) {
        console.error('上传失败:', error);
        this.$message.error(error.message || '图片上传失败');
        // 标记上传失败
        this.previews[previewIndex].uploading = false;
        this.previews[previewIndex].error = true;
      }
    },
    updatePreviewProgress(index, progress) {
      if (this.previews[index]) {
        this.previews[index].progress = progress;
      }
    },
    removePreview(index) {
      this.previews.splice(index, 1);
      this.uploadedUrls.splice(index, 1);
      this.$emit('upload-success', this.uploadedUrls);
    },
    resetFileInput() {
      this.$refs.fileInput.value = '';
    },
    // 清空所有图片
    clearImages() {
      this.previews = [];
      this.uploadedUrls = [];
      this.$emit('upload-success', this.uploadedUrls);
    }
  }
};
</script>

<style scoped>
.image-upload-container {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.image-preview-list {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
}

.image-preview-item {
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 8px;
  /* overflow: hidden; */
  border: 2px solid #e5e5e5;
}

.image-preview-item.add-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
  border: 2px dashed #d9d9d9;
  cursor: pointer;
}

.add-icon {
  font-size: 32px;
  color: #999;
  margin-bottom: 8px;
}

.add-text {
  font-size: 14px;
  color: #999;
}

.preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-remove {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: rgba(255, 0, 0, 0.7);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
}

.item-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 5px;
  background-color: rgba(0, 0, 0, 0.5);
}

.hidden-input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
</style>